import { useQuery } from "convex/react";
import { Building2, Users } from "lucide-react";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Loader,
  PageContainer,
  PageHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui";
import { api } from "@convex/_generated/api";

/** Pastille de repli quand l'organisation n'a pas de logo renseigné. */
function PastilleInitiales({ nom }: { nom: string }) {
  const initiales = nom
    .split(" ")
    .map((mot) => mot[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
  return (
    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-surface bg-charte-wash-accent text-xl font-semibold text-charte-accent">
      {initiales || <Building2 className="h-6 w-6" />}
    </div>
  );
}

export function MonOrganisation() {
  const org = useQuery(api.organisation.monOrganisation);
  const annuaire = useQuery(api.organisation.listerMembresOrg);

  if (org === undefined || annuaire === undefined) {
    return (
      <PageContainer>
        <div className="flex justify-center p-16">
          <Loader />
        </div>
      </PageContainer>
    );
  }

  // Un membre sans groupe est un GESTIONNAIRE : dans ce modèle, l'absence de
  // groupe vaut accès à toute l'organisation (cf. `superAdmin.creerUser`), ce
  // n'est pas un rattachement oublié.
  const gestionnaires = annuaire.membres.filter((m) => !m.groupId);

  return (
    <PageContainer>
      <PageHeader
        titre="Mon organisation"
        sousTitre={
          annuaire.portee === "organisation"
            ? "Membres et groupes de votre organisation"
            : "Membres de votre groupe"
        }
      />

      <Card className="mb-6">
        <CardContent className="flex items-center gap-4 pt-6">
          {org.logoUrl ? (
            <img
              src={org.logoUrl}
              alt={`Logo ${org.nom}`}
              // Cf. sidebar : sans cet attribut, Drive renvoie 429 sur un
              // Referer localhost et le logo reste invisible en dev.
              referrerPolicy="no-referrer"
              className="max-h-16 w-auto max-w-[12rem] object-contain"
            />
          ) : (
            <PastilleInitiales nom={org.nom} />
          )}
          <div className="min-w-0">
            <p className="truncate text-xl font-semibold">{org.nom}</p>
            <p className="text-sm text-muted-foreground">
              {org.nbMembres} membre{org.nbMembres > 1 ? "s" : ""} ·{" "}
              {org.nbGroupes} groupe{org.nbGroupes > 1 ? "s" : ""}
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">
            {annuaire.portee === "organisation" ? "Groupes" : "Votre groupe"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {annuaire.groupes.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Aucun groupe n'est défini.
            </p>
          ) : (
            <div className="flex flex-wrap gap-3">
              {annuaire.groupes.map((g) => (
                <div
                  key={g._id}
                  className="flex items-center gap-2 rounded-surface border border-border px-3 py-2"
                >
                  <Users className="h-4 w-4 text-charte-accent" />
                  <span className="text-sm font-medium">{g.nom}</span>
                  <Badge variant="secondary">{g.nbMembres}</Badge>
                </div>
              ))}
              {gestionnaires.length > 0 && (
                <div className="flex items-center gap-2 rounded-surface border border-dashed border-border px-3 py-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Gestionnaires
                  </span>
                  <Badge variant="secondary">{gestionnaires.length}</Badge>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Membres</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Groupe</TableHead>
                <TableHead>Rôle</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {annuaire.membres.map((m) => (
                <TableRow key={m._id}>
                  <TableCell className="font-medium">{m.nom ?? "—"}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {m.email ?? "—"}
                  </TableCell>
                  <TableCell>
                    {m.groupeNom ?? (
                      <span className="text-muted-foreground">
                        Toute l'organisation
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {m.estAdmin ? (
                      <Badge>Administrateur</Badge>
                    ) : m.groupId ? (
                      <span className="text-muted-foreground">Membre</span>
                    ) : (
                      <Badge variant="secondary">Gestionnaire</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </PageContainer>
  );
}
